// UniqueHQ Feedback - LOCAL PREVIEW persona (dev-only; side-effect free).
//
// ============================ DEV-ONLY / LOCAL-ONLY ============================
// Everything that can act as a different person lives behind ONE compile-time switch:
//   import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK_AUTH === 'true'
// `import.meta.env.DEV` is a literal `false` in every production `vite build`, so each function below collapses to its
// "not enabled" answer and the persona table, the storage key and the request header name are dropped from the shipped bundle.
// test/feedback/feedback-production-bundle.test.mjs builds the real production bundle and fails if any of them is found in it.
//
// WHAT A PERSONA DOES. It decides (1) which Cognito groups the Feedback UI believes the signed-in person holds (so the Inbox tab
// shows or hides exactly as it would for that person), and (2) the `x-local-feedback-persona` request header that the LOCAL mock
// server (scripts/local-mock-feedback.mjs) turns into verified-looking claims for the REAL compiled Feedback handler, so the
// server-side Owner check (not just the screen) runs for that person too. The header is a test harness, NOT authentication; a
// deployed API never sees it because a production build does not send it.
// ===============================================================================

const ENABLED = import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK_AUTH === 'true';

export type LocalPersona = 'owner' | 'hq-admin' | 'user';

const PERSONAS: Record<LocalPersona, { label: string; groups: string[] }> = {
  owner: { label: 'Owner', groups: ['owner'] },
  // An HQ admin who is NOT the Owner: proves the admin group alone never opens the Feedback inbox.
  'hq-admin': { label: 'HQ Admin (not Owner)', groups: ['admin'] },
  user: { label: 'Regular user', groups: ['designer'] },
};

const STORAGE_KEY = 'local-feedback-persona';

/** True only under `vite dev` with the explicit VITE_LOCAL_MOCK_AUTH opt-in. */
export function localPreviewEnabled(): boolean {
  return ENABLED;
}

/** The persona chosen in the preview bar (default: owner). Always 'owner' when the preview is not enabled. */
export function currentLocalPersona(): LocalPersona {
  if (!ENABLED) return 'owner';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'hq-admin' || v === 'user' || v === 'owner' ? v : 'owner';
  } catch {
    return 'owner';
  }
}

export function setLocalPersona(next: LocalPersona): void {
  if (!ENABLED) return;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch { /* storage unavailable */ }
}

/** The groups the Feedback UI should believe. Unchanged (the real groups) unless the preview is enabled. */
export function feedbackGroups(realGroups: string[]): string[] {
  if (!ENABLED) return realGroups;
  return PERSONAS[currentLocalPersona()].groups;
}

export function localPersonaLabels(): Array<{ id: LocalPersona; label: string }> {
  if (!ENABLED) return [];
  return (['owner', 'hq-admin', 'user'] as LocalPersona[]).map((id) => ({ id, label: PERSONAS[id].label }));
}

/** Extra request headers for the local mock server. Empty (and the header name absent from the bundle) unless the preview is enabled. */
export function localPreviewHeaders(): Record<string, string> {
  if (!ENABLED) return {};
  return { 'x-local-feedback-persona': currentLocalPersona() };
}
